import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const EMPTY = { name: "", select_type: "single", required: false, min_select: 0, max_select: 1, options: [{ name: "", price_delta: 0 }] };

export function modifierSummary(g) {
  if (!g) return "";
  const rule = g.select_type === "single" ? (g.required ? "pick one (required)" : "pick one") : `pick ${g.min_select || 0}–${g.max_select || "∞"}`;
  return `${(g.options || []).length} options · ${rule}`;
}

/* Checklist used in the product editor to attach global modifier groups */
export function ModifierAttach({ groups = [], valueIds = [], onChange, onManage }) {
  const toggle = (id) => {
    const set = valueIds.includes(id) ? valueIds.filter((x) => x !== id) : [...valueIds, id];
    onChange(set);
  };
  return (
    <div className="border border-border p-3" data-testid="modifier-attach">
      {groups.length === 0 ? (
        <p className="text-xs text-muted-foreground">No modifier groups yet. Create ones like “Size” or “Toppings” to reuse across products.</p>
      ) : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {groups.map((g) => (
            <label key={g.id} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`modifier-attach-${g.id}`}>
              <input type="checkbox" className="mt-1" checked={valueIds.includes(g.id)} onChange={() => toggle(g.id)} />
              <span><span className="font-semibold">{g.name}</span> <span className="text-muted-foreground text-xs">· {modifierSummary(g)}</span></span>
            </label>
          ))}
        </div>
      )}
      <button type="button" onClick={onManage} className="text-xs text-primary hover:underline mt-2" data-testid="open-modifiers-manager-btn">Manage modifiers</button>
    </div>
  );
}

export function ModifiersManager({ open, onOpenChange, groups = [], reload }) {
  const [form, setForm] = useState(null); // null = list view; object = editing
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const startNew = () => { setForm({ ...EMPTY, options: [{ name: "", price_delta: 0 }] }); setEditId(null); };
  const startEdit = (g) => {
    setForm({ name: g.name, select_type: g.select_type || "single", required: !!g.required,
      min_select: g.min_select || 0, max_select: g.max_select || (g.select_type === "single" ? 1 : 0),
      options: (g.options || []).map((o) => ({ id: o.id, name: o.name, price_delta: o.price_delta })) });
    setEditId(g.id);
  };
  const setOpt = (i, patch) => setForm((f) => ({ ...f, options: f.options.map((o, k) => k === i ? { ...o, ...patch } : o) }));
  const addOpt = () => setForm((f) => ({ ...f, options: [...f.options, { name: "", price_delta: 0 }] }));
  const delOpt = (i) => setForm((f) => ({ ...f, options: f.options.filter((_, k) => k !== i) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name the modifier group"); return; }
    const opts = form.options.filter((o) => (o.name || "").trim());
    if (!opts.length) { toast.error("Add at least one option"); return; }
    const payload = {
      name: form.name.trim(), select_type: form.select_type, required: form.required,
      min_select: form.select_type === "multi" ? (parseInt(form.min_select, 10) || 0) : (form.required ? 1 : 0),
      max_select: form.select_type === "single" ? 1 : (parseInt(form.max_select, 10) || 0),
      options: opts.map((o) => ({ id: o.id, name: o.name.trim(), price_delta: parseFloat(o.price_delta) || 0 })),
    };
    setBusy(true);
    try {
      if (editId) await opApi.put(`/tenant/modifier-groups/${editId}`, payload);
      else await opApi.post("/tenant/modifier-groups", payload);
      toast.success("Saved"); setForm(null); setEditId(null); await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (g) => {
    if (!window.confirm(`Delete “${g.name}”? It will be removed from all products.`)) return;
    try { await opApi.delete(`/tenant/modifier-groups/${g.id}`); await reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setForm(null); onOpenChange(v); }}>
      <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid="modifiers-manager">
        <DialogHeader>
          <DialogTitle className="font-display">Product modifiers</DialogTitle>
          <DialogDescription>Reusable choices like Size, Milk or Toppings. Attach them to products in the product editor.</DialogDescription>
        </DialogHeader>

        {!form ? (
          <>
            <div className="border border-border divide-y divide-border">
              {groups.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No modifier groups yet.</div> :
                groups.map((g) => (
                  <div key={g.id} className="flex items-center gap-2 px-3 py-2" data-testid={`modifier-row-${g.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{g.name}</div>
                      <div className="text-xs text-muted-foreground">{modifierSummary(g)} · {(g.options || []).map((o) => o.name + (o.price_delta ? ` (+${money(o.price_delta)})` : "")).join(", ")}</div>
                    </div>
                    <button className="text-muted-foreground hover:text-foreground p-1" onClick={() => startEdit(g)} data-testid={`modifier-edit-${g.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button className="text-muted-foreground hover:text-destructive p-1" onClick={() => del(g)} data-testid={`modifier-del-${g.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
            </div>
            <Button className="rounded-none gap-1 w-full" onClick={startNew} data-testid="modifier-add-btn"><Plus className="h-4 w-4" /> New modifier group</Button>
          </>
        ) : (
          <div className="space-y-3">
            <div><Label>Name</Label><Input className="rounded-none mt-1" placeholder="Size / Milk / Toppings" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="modifier-name-input" /></div>
            <div className="flex gap-4 items-center">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="modifier-type-single">
                <input type="radio" checked={form.select_type === "single"} onChange={() => setForm({ ...form, select_type: "single", max_select: 1 })} /> Single choice
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="modifier-type-multi">
                <input type="radio" checked={form.select_type === "multi"} onChange={() => setForm({ ...form, select_type: "multi" })} /> Multi-select
              </label>
            </div>
            {form.select_type === "single" ? (
              <div className="flex items-center gap-2">
                <Switch checked={form.required} onCheckedChange={(v) => setForm({ ...form, required: v })} data-testid="modifier-required-toggle" />
                <Label className="text-sm font-normal">Required — customer must pick one</Label>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Min to pick (0 = optional)</Label><Input type="number" min="0" className="rounded-none mt-1" value={form.min_select} onChange={(e) => setForm({ ...form, min_select: e.target.value })} data-testid="modifier-min-input" /></div>
                <div><Label className="text-xs">Max to pick (0 = no limit)</Label><Input type="number" min="0" className="rounded-none mt-1" value={form.max_select} onChange={(e) => setForm({ ...form, max_select: e.target.value })} data-testid="modifier-max-input" /></div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Options</Label>
              <div className="space-y-2 mt-2">
                {form.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`modifier-option-${i}`}>
                    <Input className="rounded-none flex-1" placeholder="Option name (e.g. Large)" value={o.name} onChange={(e) => setOpt(i, { name: e.target.value })} data-testid={`modifier-option-name-${i}`} />
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-muted-foreground">+$</span>
                      <Input type="number" step="0.01" className="rounded-none w-20" placeholder="0.00" value={o.price_delta} onChange={(e) => setOpt(i, { price_delta: e.target.value })} data-testid={`modifier-option-price-${i}`} />
                    </div>
                    <button className="text-muted-foreground hover:text-destructive p-1" onClick={() => delOpt(i)} disabled={form.options.length <= 1}><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" className="rounded-none gap-1 mt-2" onClick={addOpt} data-testid="modifier-add-option-btn"><Plus className="h-3 w-3" /> Add option</Button>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" className="rounded-none" onClick={() => { setForm(null); setEditId(null); }}>Cancel</Button>
              <Button className="rounded-none" disabled={busy} onClick={save} data-testid="modifier-save-btn">Save group</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
